/**
 * NymoArt QR Scanner - JavaScript v2.0.0
 * 
 * Custom Element for QR code scanning and design voting
 * 
 * @requires NymoArt (nymo-base.js)
 * @requires jsQR (loaded dynamically)
 */

if (!customElements.get('nymo-qr-scanner')) {
  customElements.define('nymo-qr-scanner', class NymoQRScanner extends HTMLElement {
    
    constructor() {
      super();
      
      this.moduleId = this.dataset.moduleId || NymoArt.utils.uniqueId('qr-');
      
      // State
      this.state = {
        isScanning: false,
        currentDesign: null,
        stream: null,
        animationFrame: null,
        abortController: null
      };
      
      // Element references (populated in connectedCallback)
      this.elements = {};
      
      // Bound methods for event listeners
      this._onStartScan = this.startScanning.bind(this);
      this._onClose = this.resetToScanner.bind(this);
      this._onCodeInput = this._handleCodeInput.bind(this);
      this._onVote = this._handleVote.bind(this);
      this._onKeydown = this._handleKeydown.bind(this);
    }
    
    connectedCallback() {
      // Get element references
      this.elements = {
        scannerView: this.querySelector('[data-view="scanner"]'),
        designView: this.querySelector('[data-view="design"]'),
        video: this.querySelector('video'),
        startBtn: this.querySelector('[data-action="start-scan"]'),
        closeBtn: this.querySelector('[data-action="close"]'),
        designImage: this.querySelector('[data-design-image]'),
        loading: this.querySelector('[data-loading]'),
        infoRow: this.querySelector('[data-info-row]'),
        title: this.querySelector('[data-title]'),
        subtitle: this.querySelector('[data-subtitle]'),
        codeInput: this.querySelector('[data-code-input]'),
        stats: this.querySelector('[data-stats]'),
        voteButtons: this.querySelectorAll('[data-action="vote"]')
      };
      
      // Bind events
      this._bindEvents();
      
      // Register module
      NymoArt.modules.register(`qr-scanner-${this.moduleId}`, this);
      
      // Check URL parameter
      this._checkUrlParameter();
      
      NymoArt.log.info(`QR Scanner initialized: ${this.moduleId}`);
    }
    
    disconnectedCallback() {
      // Cleanup
      this.stopScanning();
      this._unbindEvents();
      NymoArt.modules.unregister(`qr-scanner-${this.moduleId}`);
    }
    
    _bindEvents() {
      this.elements.startBtn?.addEventListener('click', this._onStartScan);
      this.elements.closeBtn?.addEventListener('click', this._onClose);
      this.elements.codeInput?.addEventListener('input', this._onCodeInput);
      
      this.elements.voteButtons.forEach(btn => {
        btn.addEventListener('click', this._onVote);
      });
      
      document.addEventListener('keydown', this._onKeydown);
    }
    
    _unbindEvents() {
      this.elements.startBtn?.removeEventListener('click', this._onStartScan);
      this.elements.closeBtn?.removeEventListener('click', this._onClose);
      this.elements.codeInput?.removeEventListener('input', this._onCodeInput);
      
      this.elements.voteButtons.forEach(btn => {
        btn.removeEventListener('click', this._onVote);
      });
      
      document.removeEventListener('keydown', this._onKeydown);
    }
    
    _handleCodeInput(e) {
      const code = e.target.value.trim().toUpperCase();
      if (code.length >= 8) {
        this.loadDesignById(code);
      }
    }
    
    _handleVote(e) {
      const voteType = e.currentTarget.dataset.voteType;
      this.submitVote(voteType);
    }
    
    _handleKeydown(e) {
      // Only handle if we have a current design
      if (!this.state.currentDesign) return;
      
      // Only handle if focus is within this component or body
      if (!this.contains(document.activeElement) && document.activeElement !== document.body) return;
      
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.submitVote('like');
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.submitVote('dislike');
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.resetToScanner();
      }
    }
    
    _checkUrlParameter() {
      const urlParams = new URLSearchParams(window.location.search);
      const codeFromUrl = urlParams.get('code');
      if (codeFromUrl) {
        NymoArt.log.info(`Code from URL: ${codeFromUrl}`);
        this.loadDesignById(codeFromUrl);
      }
    }
    
    // ===== QR SCANNING =====
    
    async loadQRLibrary() {
      return new Promise((resolve, reject) => {
        if (window.jsQR) {
          resolve(window.jsQR);
          return;
        }
        
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';
        script.onload = () => resolve(window.jsQR);
        script.onerror = () => reject(new Error('Failed to load jsQR library'));
        document.head.appendChild(script);
      });
    }
    
    async startScanning() {
      if (this.state.isScanning) return;
      
      try {
        const jsQR = await this.loadQRLibrary();
        
        // Get camera stream
        this.state.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });
        
        this.elements.video.srcObject = this.state.stream;
        this.elements.video.dataset.active = 'true';
        this.elements.startBtn.hidden = true;
        this.dataset.scanning = 'true';
        
        this.state.isScanning = true;
        
        // Create canvas for QR detection
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        
        const scanFrame = () => {
          if (!this.state.isScanning) return;
          
          if (this.elements.video.readyState === this.elements.video.HAVE_ENOUGH_DATA) {
            canvas.width = this.elements.video.videoWidth;
            canvas.height = this.elements.video.videoHeight;
            context.drawImage(this.elements.video, 0, 0);
            
            const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
            const qrResult = jsQR(imageData.data, imageData.width, imageData.height, {
              inversionAttempts: 'dontInvert'
            });
            
            if (qrResult) {
              NymoArt.log.info('QR Code detected:', qrResult.data);
              const designId = this._extractDesignIdFromQR(qrResult.data);
              
              if (designId) {
                this.loadDesignById(designId, 'qr');
                return;
              }
            }
          }
          
          if (this.state.isScanning) {
            this.state.animationFrame = requestAnimationFrame(scanFrame);
          }
        };
        
        scanFrame();
        
      } catch (error) {
        NymoArt.log.error('Camera error:', error);
        this.elements.loading.textContent = 'Kamera-Zugriff fehlgeschlagen';
        this.elements.loading.hidden = false;
        this.elements.startBtn.hidden = false;
      }
    }
    
    stopScanning() {
      this.state.isScanning = false;
      this.dataset.scanning = 'false';
      
      if (this.state.stream) {
        this.state.stream.getTracks().forEach(track => track.stop());
        this.state.stream = null;
      }
      
      if (this.state.animationFrame) {
        cancelAnimationFrame(this.state.animationFrame);
        this.state.animationFrame = null;
      }
      
      if (this.elements.video) {
        this.elements.video.dataset.active = 'false';
        this.elements.video.srcObject = null;
      }
    }
    
    _extractDesignIdFromQR(qrText) {
      try {
        // Try URL parsing
        const url = new URL(qrText);
        const codeParam = url.searchParams.get('code');
        if (codeParam) return codeParam;
      } catch (e) {
        // Not a URL
      }
      
      // Regex fallback for ?code= or &code=
      const codeMatch = qrText.match(/[?&]code=([a-zA-Z0-9_-]+)/i);
      if (codeMatch) return codeMatch[1];
      
      // Direct ID (alphanumeric, <50 chars)
      if (/^[a-zA-Z0-9_-]+$/.test(qrText) && qrText.length < 50) {
        return qrText;
      }
      
      // URL path pattern
      const urlMatch = qrText.match(/\/design\/([a-zA-Z0-9_-]+)/);
      if (urlMatch) return urlMatch[1];
      
      return null;
    }
    
    // ===== DESIGN LOADING =====
    
    async loadDesignById(designId, source = 'manual') {
      NymoArt.log.info(`Loading design: ${designId}`);
      
      // Cancel previous request
      if (this.state.abortController) {
        this.state.abortController.abort();
      }
      this.state.abortController = new AbortController();
      
      // Stop scanning
      this.stopScanning();
      
      // Switch to design view
      this.elements.scannerView.hidden = true;
      this.elements.designView.hidden = false;
      this.elements.loading.hidden = false;
      this.elements.loading.textContent = 'Lade Design...';
      this.elements.designImage.dataset.loaded = 'false';
      
      try {
        // Load from Firebase
        const imageData = await NymoArt.firebase.getDoc(
          'image', 
          designId, 
          this.state.abortController.signal
        );
        
        if (!imageData.imageUrl) {
          throw new Error('Design-Bild nicht verfügbar');
        }
        
        // Store current design
        this.state.currentDesign = {
          designId,
          ...imageData
        };
        
        // Load image
        await this._loadImage(imageData.imageUrl);
        
        // Update UI
        this._updateDesignUI(imageData);
        
        // Emit event with full data
        NymoArt.events.emit('nymo:code:detected', {
          designId,
          source,
          imageData: this.state.currentDesign
        });
        
        // Load vote stats
        await this._loadVoteStats(designId);
        
        // Track view
        this._trackView(designId);
        
      } catch (error) {
        if (error.name === 'AbortError') {
          NymoArt.log.info('Request cancelled');
          return;
        }
        
        NymoArt.log.error('Design loading failed:', error);
        this.elements.loading.textContent = `Fehler: ${error.message}`;
        this.elements.codeInput.style.borderColor = '#f44336';
      }
    }
    
    _loadImage(url) {
      return new Promise((resolve, reject) => {
        this.elements.designImage.onload = () => {
          this.elements.designImage.dataset.loaded = 'true';
          this.elements.loading.hidden = true;
          resolve();
        };
        this.elements.designImage.onerror = () => {
          reject(new Error('Bild konnte nicht geladen werden'));
        };
        this.elements.designImage.src = url;
      });
    }
    
    _updateDesignUI(imageData) {
      // Update title
      const owner = imageData.owner?.trim();
      const artist = imageData.artist?.trim();
      
      if (owner) {
        this.elements.title.textContent = `Owner: ${owner}`;
      } else if (artist) {
        this.elements.title.textContent = `Artist: ${artist}`;
      } else {
        this.elements.title.textContent = imageData.designId;
      }
      
      // Update subtitle
      if (this.elements.subtitle) {
        this.elements.subtitle.textContent = `${imageData.collectionId || ''} #${imageData.designId}`;
      }
      
      // Show info row and vote buttons
      if (this.elements.infoRow) {
        this.elements.infoRow.hidden = false;
        this.elements.infoRow.dataset.noDesign = 'false';
      }
      this.elements.voteButtons.forEach(btn => btn.hidden = false);
    }
    
    async _loadVoteStats(designId) {
      try {
        const voteData = await NymoArt.firebase.getDoc('votes', designId);
        const likes = voteData.likes || 0;
        const dislikes = voteData.dislikes || 0;
        const total = likes + dislikes;
        
        this.elements.stats.textContent = total > 0
          ? `✓ ${likes}   (${total} Votes)    ✗ ${dislikes}`
          : 'Noch keine Votes';
          
      } catch (error) {
        // No votes yet
        this.elements.stats.textContent = 'Noch keine Votes';
      }
    }
    
    async _trackView(designId) {
      try {
        let currentViews = 0;
        try {
          const viewData = await NymoArt.firebase.getDoc('views', designId);
          currentViews = viewData.count || 0;
        } catch (e) {
          // No views yet
        }
        
        await NymoArt.firebase.updateDoc('views', designId, {
          count: currentViews + 1,
          lastSeen: new Date(),
          designId: designId,
          userAgent: navigator.userAgent,
          referrer: document.referrer || 'direct'
        });
        
        NymoArt.log.info(`View tracked: ${designId} → ${currentViews + 1}`);
      } catch (error) {
        NymoArt.log.warn('View tracking failed:', error);
      }
    }
    
    // ===== VOTING =====
    
    async submitVote(voteType) {
      if (!this.state.currentDesign) return;
      
      const { designId, collectionId, layoutVariant } = this.state.currentDesign;
      NymoArt.log.info(`Submitting vote: ${voteType} for ${designId}`);
      
      try {
        // Get current votes
        let currentLikes = 0;
        let currentDislikes = 0;
        
        try {
          const voteData = await NymoArt.firebase.getDoc('votes', designId);
          currentLikes = voteData.likes || 0;
          currentDislikes = voteData.dislikes || 0;
        } catch (e) {
          // No votes yet
        }
        
        const newLikes = voteType === 'like' ? currentLikes + 1 : currentLikes;
        const newDislikes = voteType === 'dislike' ? currentDislikes + 1 : currentDislikes;
        
        // Update votes
        await NymoArt.firebase.updateDoc('votes', designId, {
          likes: newLikes,
          dislikes: newDislikes,
          designId: designId,
          collectionId: collectionId || '',
          layoutVariant: layoutVariant || '',
          lastVoted: new Date(),
          lastUserAgent: navigator.userAgent
        });
        
        // Emit event
        NymoArt.events.emit('nymo:vote:submitted', {
          designId,
          voteType,
          newLikes,
          newDislikes
        });
        
        // Animate buttons
        this._animateVote(voteType);
        
      } catch (error) {
        NymoArt.log.error('Vote failed:', error);
      }
    }
    
    _animateVote(voteType) {
      const clickedBtn = voteType === 'like' 
        ? this.querySelector('[data-vote-type="like"]')
        : this.querySelector('[data-vote-type="dislike"]');
      const otherBtn = voteType === 'like'
        ? this.querySelector('[data-vote-type="dislike"]')
        : this.querySelector('[data-vote-type="like"]');
      
      // Hide other button immediately
      otherBtn.style.opacity = '0';
      otherBtn.style.pointerEvents = 'none';
      clickedBtn.style.pointerEvents = 'none';
      
      // After 1 second, hide clicked button and row
      setTimeout(() => {
        clickedBtn.style.opacity = '0';
        
        setTimeout(() => {
          this.elements.infoRow.dataset.noDesign = 'true';
          // Reset for next vote
          clickedBtn.style.opacity = '1';
          otherBtn.style.opacity = '1';
          clickedBtn.style.pointerEvents = 'auto';
          otherBtn.style.pointerEvents = 'auto';
        }, 300);
      }, 1000);
    }
    
    // ===== RESET =====
    
    resetToScanner() {
      NymoArt.log.info('Resetting to scanner');
      
      // Stop any scanning
      this.stopScanning();
      
      // Switch views
      this.elements.designView.hidden = true;
      this.elements.scannerView.hidden = false;
      this.elements.startBtn.hidden = false;
      this.elements.loading.hidden = true;
      
      // Reset UI
      this.elements.codeInput.value = '';
      this.elements.codeInput.style.borderColor = '';
      this.elements.stats.textContent = '';
      this.elements.designImage.src = '';
      this.elements.designImage.dataset.loaded = 'false';
      
      // Hide info row and vote buttons
      this.elements.infoRow.hidden = true;
      this.elements.infoRow.dataset.noDesign = 'true';
      this.elements.voteButtons.forEach(btn => btn.hidden = true);
      
      // Clear state
      this.state.currentDesign = null;
      
      // Emit reset event
      NymoArt.events.emit('nymo:code:reset', {});
    }
  });
  
  console.log('🔍 NymoArt QR Scanner v2.0.0 loaded');
}

